import { idn } from '@wix/typesafe-inversify';
import type { EditorApp } from '../services/EditorApp/EditorApp';
import type {
  IEditorSDK,
  IPlatformEssentials,
  IPlatformHttpClient,
  IPlatformPanorama,
  IPlatformTranslations,
  IPlatformWixExperiments,
} from '../types/PlatformTypes';
import type { IAddWidgetService } from '../services/AddWidgetService/IAddWidgetService';
import type { IBobWidgetManifestFetcher } from '../services/BobWidgetServices/IBobWidgetManifestFetcher';
import type { IBobWidgetImages } from '../services/BobWidgetServices/IBobWidgetImages';
import type { IConsoleLogger } from '../services/ConsoleLogger/IConsoleLogger';
import type { IAppDescriptorService } from '../services/AppDescriptorService/IAppDescriptorService';
import type { IExperiments } from '../services/Experiments/IExperiments';
import type { IFedopsLogger } from '../services/FedopsLogger/FedopsLogger';
import type { IPagesService } from '../services/PagesService/IPagesService';
import type { IReadyAppData } from '../services/ReadyAppData/IReadyAppData';
import type { ITranslations } from '../services/Translations/ITranslations';
import type { IPanelHandlers } from '../services/PanelHandlers/IPanelHandlers';
import type { IMasterStructureFetcher } from '../services/MasterStructureFetcher/IMasterStructureFetcher';
import type { IEditorTransactions } from '../services/EditorTransactions/IEditorTransactions';
import type { IManifestBuilder } from '../services/ManifestBuilder/IManifestBuilder';
import type { IPanelsApiFactory } from '@wix/blocks-widget-services/panels';
import type { IMonitoring } from '../services/Monitoring/IMonitoring';
import type { ITransaction } from '../services/Monitoring/ITransaction';
import type { ICommonSessionDataProvider } from '../services/Monitoring/CommonSessionDataProvider/ICommonSessionDataProvider';
import type { IPromiseVoider } from '../services/PromiseVoider/IPromiseVoider';
import type { IAppDescriptorApi } from '@wix/blocks-widget-services/src/editor/remoteStructureFetcher/types';

export const Services = {
  PlatformEssentials: idn<IPlatformEssentials>('PlatformEssentials'),
  PlatformPanorama: idn<IPlatformPanorama>('PlatformPanorama'),
  PlatformHttpClient: idn<IPlatformHttpClient>('PlatformHttpClient'),
  PlatformTranslations: idn<IPlatformTranslations>('PlatformTranslations'),
  PlatformWixExperiments: idn<IPlatformWixExperiments>(
    'PlatformWixExperiments',
  ),
  AppDescriptorApi: idn<IAppDescriptorApi>('AppDescriptorApi'),
  FedopsLogger: idn<IFedopsLogger>('FedopsLogger'),
  EditorApp: idn<EditorApp>('EditorApp'),
  EditorSDK: idn<IEditorSDK>('EditorSDK'),
  AddWidgetService: idn<IAddWidgetService>('AddWidgetService'),
  AppDescriptorService: idn<IAppDescriptorService>('AppDescriptorService'),
  BobWidgetManifestFetcher: idn<IBobWidgetManifestFetcher>(
    'BobWidgetManifestFetcher',
  ),
  BobWidgetImages: idn<IBobWidgetImages>('BobWidgetImages'),
  ConsoleLogger: idn<IConsoleLogger>('ConsoleLogger'),
  Experiments: idn<IExperiments>('Experiments'),
  PagesService: idn<IPagesService>('PagesService'),
  PanelHandlers: idn<IPanelHandlers>('PanelHandlers'),
  ReadyAppData: idn<IReadyAppData>('ReadyAppData'),
  Translations: idn<ITranslations>('Translations'),
  MasterStructureFetcher: idn<IMasterStructureFetcher>(
    'MasterStructureFetcher',
  ),
  EditorTransactions: idn<IEditorTransactions>('EditorTransactions'),
  ManifestBuilder: idn<IManifestBuilder>('ManifestBuilder'),
  PanelsApiFactory: idn<IPanelsApiFactory>('PanelsApiFactory'),
  Monitoring: idn<IMonitoring>('Monitoring'),
  Transaction: idn<ITransaction>('Transaction'),
  TransactionFactory: idn<(name: string) => ITransaction>('TransactionFactory'),
  CommonSessionDataProvider: idn<ICommonSessionDataProvider>(
    'CommonSessionDataProvider',
  ),
  PromiseVoider: idn<IPromiseVoider>('PromiseVoider'),
} as const;
